import React, { createContext, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import io from 'socket.io-client';
import { hmsToSeconds } from '../../utils';
import {
  _process3210,
  _process3220,
  _process3250,
  _process3310,
  _process6210,
  _process6220,
  _process9100,
} from '../../utils/priceboard';
import {
  receive1101,
  receive3211,
  receiveReregister,
  serverOff,
  socketReady,
} from './actions';

const WebSocketContext = createContext(null);
export { WebSocketContext };

const BATCH_SIZE = 12;
const UPDATE_INTERVAL = 50; // 5 * 10ms
const PING_INTERVAL = 10000; // 10 seconds
const MARKET_CLOSE_TIME = '15:00:00';

export default ({ children, store }) => {
  const socketRef = useRef(null);
  const wsRef = useRef(null);
  const renderQueueRef = useRef([]);
  const mountedRef = useRef(false);
  const timerRef = useRef(null);
  const intervalRef = useRef(null);
  const countPingRef = useRef(0);

  const dispatch = useDispatch();

  const processUpdateCell = useCallback(() => {    
    mountedRef.current = true;
    const batch = renderQueueRef.current.splice(0, BATCH_SIZE);
    mountedRef.current = false;

    batch.forEach((element) => {
      switch (element.id) {
        case 3210:
          _process3210(element);
          break;
        case 3220:
          _process3220(element);
          break;
        case 3250:
          _process3250(element);
          break;
        case 3310:
          _process3310(element);
          break;
        default:
          break;
      }
    });
  }, []);

  const processTimeIndex = useCallback((msg1101) => {
    const diffSecond = hmsToSeconds(msg1101.time) - hmsToSeconds(MARKET_CLOSE_TIME);
    if (diffSecond > 0 && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      renderQueueRef.current = [];
    }
  }, []);

  const sendPing = useCallback(() => {
    if (!socketRef.current) return;

    countPingRef.current++;
    console.log('ping server', countPingRef.current);

    const msg = { action: 'ping', mode: 'sync', data: ' ' };
    socketRef.current.emit('regs', JSON.stringify(msg), (data) => {
      dispatch(socketReady());
      countPingRef.current = 0;
      if (data === '6666') dispatch(receiveReregister(true));
    });
  }, [dispatch]);

  const sendMessage = useCallback(
    (message) => {
      if (!socketRef.current) {
        console.warn('Socket not initialized');
        return;
      }

      console.log('Sending message:', message);
      const payload = JSON.stringify(message);

      if (message.action === 'join') {
        socketRef.current.emit('regs', payload, (dt) => {
          console.log('Join response:', dt);
          dispatch({ type: dt === 'OK' ? 'REGISTER_OK' : 'REGISTER_NOT_OK' });
        });
      } else {
        socketRef.current.emit('regs', payload);
      }
    },
    [dispatch]
  );

  const reconnectTrigger = useCallback(() => {
    if (socketRef.current) {
      console.log('Triggering reconnect');
      socketRef.current.connect();
    }
  }, []);

  const handleActivity = useCallback(() => {
    console.log('Clearing queue due to visibility change');
    renderQueueRef.current = [];
  }, []);

  const handleChange = useCallback(() => {
    const pathname = window.location.hash;
    const diffSecond =
      hmsToSeconds(new Date().toTimeString().slice(0, 8)) - hmsToSeconds(MARKET_CLOSE_TIME);

    console.log('Hash change:', pathname, diffSecond, timerRef.current);
    if (pathname.includes('/home/bang-gia') && diffSecond < 0) {
      if (!timerRef.current) {
        timerRef.current = setInterval(processUpdateCell, UPDATE_INTERVAL);
      }
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      renderQueueRef.current = [];
    }
  }, [processUpdateCell]);

  const initSocket = useCallback(() => {
    if (socketRef.current) return;

    const { client: { config: { config: { ws } } } } = store.getState();
    socketRef.current = io.connect(ws, {
      autoConnect: false,
      reconnection: false,
    });

    socketRef.current.on('connect', () => {
      console.log('Socket connected');
      dispatch(socketReady());
    });

    socketRef.current.on('reconnect', () => {
      console.log('Socket reconnected');
      dispatch(socketReady());
    });

    socketRef.current.on('disconnect', () => {
      console.log('Socket disconnected');
      dispatch(serverOff());
      initSocket(); // Attempt to reinitialize
    });

    socketRef.current.on('reconnect_failed', () => {
      console.log('Reconnect failed');
      dispatch(serverOff());
    });

    socketRef.current.on('reconnect_error', () => {
      console.log('Reconnect error');
      dispatch(serverOff());
    });

    socketRef.current.on('public', (msg) => {
      const payload = msg.data;
      if (!payload) return;

      switch (payload.id) {
        case 6666:
          dispatch(receiveReregister(true));
          break;
        case 1101:
          processTimeIndex(payload);
          dispatch(socketReady());
          dispatch(receive1101(payload));
          break;
        case 3211:
          dispatch(receive3211(payload));
          break;
        case 3210:
        case 3220:
        case 3223:
        case 3250:
          if (!mountedRef.current) renderQueueRef.current.push(payload);
          break;
        case 9100:
          _process9100(payload);
          break;
        case 3310:
          _process3310(payload);
          break;
        case 6210:
          _process6210(payload);
          break;
        case 6220:
          _process6220(payload);
          break;
        default:
          break;
      }
    });

    socketRef.current.open((err) => {
      if (err) {
        console.error('Socket connection error:', err);
        dispatch(serverOff());
      } else {
        dispatch(socketReady());
      }
    });

    intervalRef.current = setInterval(sendPing, PING_INTERVAL);
  }, [dispatch, processTimeIndex, sendPing, store]);

  React.useEffect(() => {
    const diffSecond =
      hmsToSeconds(new Date().toTimeString().slice(0, 8)) - hmsToSeconds(MARKET_CLOSE_TIME);
    if (diffSecond < 0) {
      timerRef.current = setInterval(processUpdateCell, UPDATE_INTERVAL);
    }

    window.addEventListener('hashchange', handleChange);
    document.addEventListener('visibilitychange', handleActivity);

    return () => {
      window.removeEventListener('hashchange', handleChange);
      document.removeEventListener('visibilitychange', handleActivity);

      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      renderQueueRef.current = [];
    };
  }, [handleActivity, handleChange, processUpdateCell]);

  wsRef.current = {
    socket: socketRef.current,
    sendMessage,
    reconnectTrigger,
    init: initSocket,
  };

  return (
    <WebSocketContext.Provider value={wsRef.current}>
      {children}
    </WebSocketContext.Provider>
  );
};